/** @format — Shared filter trigger + MUI popover */

import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { Popover } from "@mui/material";
import { cn } from "@/lib/utils";

function useIsMobileViewport() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return isMobile;
}

export default function FilterDropdown({
  label,
  valueLabel,
  children,
  compactLabel,
  buttonClassName,
  active = false,
  menuWidth,
  mobileCenter = false,
}: {
  label: string;
  valueLabel?: string;
  children: (close: () => void) => React.ReactNode;
  compactLabel?: string;
  buttonClassName?: string;
  active?: boolean;
  menuWidth?: number;
  mobileCenter?: boolean;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  const close = () => setAnchor(null);
  const displayLabel = valueLabel || label;
  const isMobile = useIsMobileViewport();
  const centerOnMobile = mobileCenter && isMobile;

  return (
    <>
      <button
        type="button"
        onClick={(event) => setAnchor(event.currentTarget)}
        className={cn(
          "box-border flex h-8 min-h-8 max-h-8 shrink-0 items-center justify-center gap-1 rounded-lg border px-1.5 text-xs font-medium leading-none text-[#0a0a0a] shadow-none transition-colors md:h-7 md:min-h-7 md:max-h-7 md:gap-1.5 md:px-2",
          active || open
            ? "border-blue-700 bg-blue-50"
            : "border-neutral-300 bg-white hover:bg-neutral-50",
          open && !active && "border-[#2563EB] shadow-[0_0_0_3px_rgba(37,99,235,0.1)]",
          buttonClassName,
        )}
      >
        <span className="truncate">
          <span className="md:hidden">{compactLabel || displayLabel}</span>
          <span className="hidden md:inline">{displayLabel}</span>
        </span>
        <ChevronDown className="h-3 w-3 shrink-0 text-[#0a0a0a]" strokeWidth={2} />
      </button>
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: centerOnMobile ? "center" : "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: centerOnMobile ? "center" : "left",
        }}
        sx={{
          "& .MuiPaper-root": {
            padding: 0,
            borderRadius: "8px",
            boxShadow: "none",
            background: "transparent",
            overflow: "visible",
            marginTop: "4px",
            minWidth: menuWidth ?? "auto",
            maxWidth: centerOnMobile ? "calc(100vw - 16px)" : "calc(100vw - 16px)",
          },
        }}
      >
        {children(close)}
      </Popover>
    </>
  );
}
